<template>
  <b-col :cols="columns.default || '12'" :lg="columns.lg || '6'" :class="classes">
    <el-dropdown
      trigger="click"
      placement="bottom-start"
      class="w-100"
      :disabled="disabled"
      @visible-change="onVisibleChange"
      @command="handleClick"
    >
      <span>
        <label :for="name">{{ label }}</label>
        <el-input
          :id="name"
          class="w-100"
          :placeholder="placeholder"
          :value="multiple ? multipleString : inputValue"
          readonly
        >
          <svg-icon
            slot="suffix"
            :icon-class="open ? 'arrow-left' : 'arrow-right'"
            class="mt-2 p-1"
          />
        </el-input>
      </span>
      <el-dropdown-menu slot="dropdown">
        <div class="p-3 position-absolute bg-white bar-header" style="z-index: 0; width: 100%">
          <strong class="text-primary d-block mb-2">{{ label }}</strong>
          <el-input v-model="search" placeholder="Search">
            <svg-icon slot="prefix" icon-class="search" class="mt-2 py-1" />
          </el-input>
        </div>
        <div v-if="!list.length" class="loading">
          <div style="margin-top: 95px"></div>
          <span v-if="error" class="text-danger"
            ><svg-icon icon-class="warning" />An error occured!</span
          >
          <span v-else><svg-icon icon-class="loading" />loading {{ name }}</span>
        </div>
        <div style="margin-top: 95px"></div>
        <div class="list">
          <span v-for="item in filteredList" :key="item.id">
            <span
              v-if="item.pa && filteredList.some((it) => it.program_area === item.name)"
              class="position-relative"
              @click="handleGroupSelect(item.name)"
            >
              <el-dropdown-item disabled>{{ item.name }}</el-dropdown-item>
              <svg-icon
                class="arrow"
                :icon-class="closedGroups[item.name] ? 'arrow-left' : 'arrow-down'"
                @click="toggleGroup($event, item.name)"
              />
            </span>
            <span v-if="!(item.pa || closedGroups[item.program_area])">
              <p v-if="multiple" class="check d-flex justify-content-between align-items-center">
                <b-form-checkbox
                  :checked="values.includes(item.id)"
                  @change="handleSelect(item.id, $event)"
                >
                  {{ item.name }}
                </b-form-checkbox>
                <small v-if="item.id === defaultSelected" class="pl-3">Selected default</small>
              </p>
              <el-dropdown-item v-else :command="item">
                <div class="d-flex justify-content-between">
                  <span>{{ item.name }}</span>
                  <small v-if="item.id === value" class="pl-3">Selected</small>
                </div>
              </el-dropdown-item>
            </span>
          </span>
        </div>
        <div>
          <slot></slot>
        </div>
        <div>
          <el-dropdown-item>
            <slot name="suffix"></slot>
          </el-dropdown-item>
        </div>
      </el-dropdown-menu>
    </el-dropdown>
  </b-col>
</template>

<script>
import Vue from "vue";

export default {
  props: {
    name: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    list: {
      type: Array,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    defaultSelected: {
      type: Number,
      default: -1,
    },
    value: {
      type: Number,
      default: -1,
    },
    values: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Object,
      default: () => ({
        default: 12,
        lg: 6,
      }),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    classes: {
      type: String,
      default: "mt-4",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      search: "",
      closedGroups: {},
    };
  },
  computed: {
    filteredList() {
      if (!this.search.trim()) return this.list;
      return this.list.filter(
        (item) =>
          item.pa ||
          item.name.toLowerCase().includes(this.search.trim().toLowerCase()) ||
          item.program_area?.toLowerCase().includes(this.search.trim().toLowerCase())
      );
    },
    inputValue() {
      return this.list.find((item) => item.id === this.value)?.name;
    },
    multipleString() {
      if (!this.values.length) return "";
      const keyword = this.list.length === this.values.length ? "All" : `${this.values.length}`;
      return `${keyword} ${this.name.replace(/s$/, "(s)")} selected`;
    },
  },
  watch: {
    list: {
      handler(values) {
        this.insertPas(values);
        this.$emit("update:value", values.find((v) => v.id == this.value)?.id || -1);
        this.$emit(
          "update:values",
          this.values.filter((v) => values.some((vl) => vl.id == v))
        );
      },
    },
  },
  mounted() {
    if (this.list.length) {
      this.insertPas(this.list);
    }
  },
  methods: {
    insertPas(value) {
      const pas = [...new Set(value.map((item) => item.program_area))];
      pas.forEach((pa) => Vue.set(this.closedGroups, pa, false));
    },
    handleClick(item) {
      if (item && !item.pa) {
        this.$emit("update:value", item.id);
      }
    },
    handleSelect(id, value) {
      if (value) {
        this.$emit("update:values", [...new Set([...this.values, id])]);
      } else {
        this.$emit("update:values", [...new Set([...this.values.filter((item) => item !== id)])]);
      }
    },
    handleGroupSelect(pa) {
      if (this.closedGroups[pa]) return this.toggleGroup(null, pa);
      const paIds = this.filteredList
        .filter((item) => item.program_area === pa)
        .map((item) => item.id);
      const selected = paIds.every((id) => this.values.includes(id));
      if (selected) {
        this.$emit("update:values", [
          ...new Set([...this.values.filter((id) => !paIds.includes(id))]),
        ]);
      } else {
        this.$emit("update:values", [...new Set([...this.values, ...paIds])]);
      }
    },
    toggleGroup(e, name) {
      this.closedGroups[name] = !this.closedGroups[name];
      e?.stopPropagation();
    },
    onVisibleChange(vis) {
      this.open = vis;
      if (!vis) {
        this.onClose();
      }
    },
  },
};
</script>

<style>
.el-dropdown-menu {
  max-height: 400px;
  /* min-height: 200px; */
  min-width: 250px;
  max-width: 500px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.bar-header {
  width: 476px;
  transform: translateY(-10px);
  z-index: 1;
}
.el-dropdown-menu .list {
  max-height: 290px;
  overflow-y: auto;
}
.is-disabled {
  background-color: rgb(198, 226, 255);
  color: rgb(64, 158, 255) !important;
  width: 100%;
  z-index: 0;
}
.check {
  margin: 8px 20px;
  font-size: 0.9rem;
}
.check * {
  cursor: pointer;
}
.loading span {
  height: 100px;
  width: 100%;
  padding: 40px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.arrow {
  position: absolute;
  right: 10px;
  top: 5px;
  padding: 5px;
  border-radius: 50%;
  cursor: pointer;
}
.arrow:hover {
  background-color: rgba(255, 255, 255, 0.4);
}
</style>
